import React, { useEffect, useState } from "react";
import api, { formatINR } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { TrendingUp, TrendingDown } from "lucide-react";
import { Toaster } from "sonner";

const today = new Date();
const firstOfFY = (() => {
    const fy = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    return `${fy}-04-01`;
})();
const todayStr = today.toISOString().slice(0, 10);

export default function AccountsProfitLoss() {
    const [data, setData] = useState(null);
    const [from, setFrom] = useState(firstOfFY);
    const [to, setTo] = useState(todayStr);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/accounts/pnl", { params: { from_date: from, to_date: to } });
            setData(data);
        } finally { setBusy(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

    const isProfit = (data?.net_profit || 0) >= 0;

    return (
        <div data-testid="accounts-pnl">
            <Toaster position="top-center" />
            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow text-sky-700 flex items-center gap-2"><TrendingUp className="w-3.5 h-3.5" />Tally-style</div>
                    <h1 className="font-serif text-4xl text-slate-900 mt-2">Profit & Loss</h1>
                    <p className="text-sm text-stone-600 mt-2">Income vs. expenses for the selected period. Sales come from invoices; other income & expenses come from your <em>Journal</em>.</p>
                </div>
            </div>

            <div className="bg-white border border-sky-100 p-4 mb-4 grid sm:grid-cols-3 gap-3 items-end">
                <div>
                    <Label className="text-[10px] uppercase tracking-widest text-stone-500">From</Label>
                    <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="pnl-from" />
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-widest text-stone-500">To</Label>
                    <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="pnl-to" />
                </div>
                <div className="text-xs text-stone-500">
                    Quick presets:
                    <button onClick={() => { const d = new Date(today.getFullYear(), today.getMonth(), 1); setFrom(d.toISOString().slice(0, 10)); setTo(todayStr); }} className="ml-2 underline text-sky-700">This month</button>
                    <button onClick={() => { setFrom(firstOfFY); setTo(todayStr); }} className="ml-3 underline text-sky-700">This FY</button>
                </div>
            </div>

            {busy && <div className="bg-white border border-sky-100 p-8 text-center text-stone-500">Calculating…</div>}

            {data && !busy && (
                <div className="grid lg:grid-cols-2 gap-4">
                    {/* INCOME */}
                    <div className="bg-white border border-sky-100">
                        <div className="px-4 py-3 bg-sky-50 border-b border-sky-100 flex items-center justify-between">
                            <h2 className="font-serif text-xl text-slate-900">Income</h2>
                            <span className="font-mono font-medium text-sky-700">{formatINR(data.income.total)}</span>
                        </div>
                        <table className="w-full text-sm">
                            <tbody>
                                <Section title="Sales">
                                    <Line label="Taxable value (sales subtotal)" amount={data.income.sales_taxable} />
                                    <Line label="Output GST collected" amount={data.income.output_gst} muted />
                                    <Line label="Sales incl. tax (gross)" amount={data.income.sales_with_tax} muted />
                                </Section>
                                <Section title="Other income">
                                    {data.income.other_income.length === 0 ? (
                                        <tr><td colSpan={2} className="px-4 py-2 text-stone-400 italic text-xs">No other income recorded — add via Journal.</td></tr>
                                    ) : data.income.other_income.map((r) => (
                                        <Line key={r.account} label={r.account} amount={r.amount} />
                                    ))}
                                    <Line label="Other income total" amount={data.income.other_income_total} bold />
                                </Section>
                                <tr className="bg-sky-50 border-t border-sky-100">
                                    <td className="px-4 py-3 font-medium text-slate-900">Total income</td>
                                    <td className="px-4 py-3 text-right font-mono font-medium text-sky-700">{formatINR(data.income.total)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* EXPENSES */}
                    <div className="bg-white border border-sky-100">
                        <div className="px-4 py-3 bg-amber-50 border-b border-amber-100 flex items-center justify-between">
                            <h2 className="font-serif text-xl text-slate-900">Expenses</h2>
                            <span className="font-mono font-medium text-amber-700">{formatINR(data.expenses.total)}</span>
                        </div>
                        <table className="w-full text-sm">
                            <tbody>
                                <Section title="MR market expenses (approved)">
                                    {data.expenses.mr_market_expenses.length === 0 ? (
                                        <tr><td colSpan={2} className="px-4 py-2 text-stone-400 italic text-xs">No approved MR expenses in this period.</td></tr>
                                    ) : data.expenses.mr_market_expenses.map((r) => (
                                        <Line key={r.account} label={r.account} amount={r.amount} />
                                    ))}
                                    <Line label="MR total" amount={data.expenses.mr_total} bold />
                                </Section>
                                <Section title="Other expenses">
                                    {data.expenses.other_expenses.length === 0 ? (
                                        <tr><td colSpan={2} className="px-4 py-2 text-stone-400 italic text-xs">No other expenses recorded — add via Journal.</td></tr>
                                    ) : data.expenses.other_expenses.map((r) => (
                                        <Line key={r.account} label={r.account} amount={r.amount} />
                                    ))}
                                    <Line label="Other expenses total" amount={data.expenses.other_expenses_total} bold />
                                </Section>
                                <tr className="bg-amber-50 border-t border-amber-100">
                                    <td className="px-4 py-3 font-medium text-slate-900">Total expenses</td>
                                    <td className="px-4 py-3 text-right font-mono font-medium text-amber-700">{formatINR(data.expenses.total)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* NET PROFIT */}
                    <div className={`lg:col-span-2 ${isProfit ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"} border-2 px-6 py-5 flex items-center justify-between`}>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-stone-600">Net {isProfit ? "profit" : "loss"} for the period</div>
                            <div className="text-xs text-stone-600 mt-1">{from} to {to} · {data.invoice_count} invoice{data.invoice_count === 1 ? "" : "s"}</div>
                        </div>
                        <div className={`flex items-center gap-3 ${isProfit ? "text-emerald-700" : "text-red-700"}`}>
                            {isProfit ? <TrendingUp className="w-7 h-7" /> : <TrendingDown className="w-7 h-7" />}
                            <span className="font-serif text-4xl font-medium" data-testid="pnl-net">{formatINR(Math.abs(data.net_profit))}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const Section = ({ title, children }) => (
    <>
        <tr className="bg-stone-50 border-b border-stone-100">
            <td colSpan={2} className="px-4 py-1.5 text-[10px] uppercase tracking-widest text-stone-500">{title}</td>
        </tr>
        {children}
    </>
);
const Line = ({ label, amount, muted = false, bold = false }) => (
    <tr className="border-b border-stone-100">
        <td className={`px-4 py-2 ${bold ? "font-medium" : ""} ${muted ? "text-stone-500 text-xs" : "text-stone-700"}`}>{label}</td>
        <td className={`px-4 py-2 text-right font-mono ${bold ? "font-medium" : ""} ${muted ? "text-stone-500 text-xs" : "text-stone-700"}`}>{formatINR(amount)}</td>
    </tr>
);
